import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Calendar,
  TrendingUp,
  Grid3X3,
  Palette,
  Plus,
  MoreVertical,
  Trash2,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  Brain,
  GitBranch,
  Trello,
  Target,
  FileText,
  Loader2,
  Info,
  MessageSquare,
  MessageCircle,
  Users,
  FolderKanban
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { TaskCreateModal } from './TaskCreateModal';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL, BACKEND_URL } from '@/config/api';

const API = `${BACKEND_URL}/api`;

// Feature legend data
const featureLegend = [
  { 
    icon: FolderKanban, 
    name: 'Boards', 
    description: 'Kanban-style project management with cards, columns, and assignments',
    visibility: 'Configurable per board',
    color: '#3B82F6'
  },
  { 
    icon: MessageSquare, 
    name: 'Channels', 
    description: 'Topic-based conversations - public or private',
    visibility: 'Public or private',
    color: '#22C55E'
  },
  { 
    icon: MessageCircle, 
    name: 'Direct Messages', 
    description: 'Private 1-on-1 conversations between two users',
    visibility: 'Only participants',
    color: '#8B5CF6'
  },
  { 
    icon: Users, 
    name: 'Groups', 
    description: 'Multi-user private conversations - like a group chat',
    visibility: 'Only invited members',
    color: '#F59E0B'
  },
  { 
    icon: Palette, 
    name: 'Canvas', 
    description: 'Infinite canvas for visual collaboration (diagrams, brainstorming)',
    visibility: 'Hub members',
    color: '#EC4899'
  },
];

export const DashboardContent = ({ user, stats, activities, quickAccess, myTasks, whiteboards = [], sharedWhiteboards = [], onRefresh }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [showLegend, setShowLegend] = useState(true); // Start visible
  const [isLegendAnimating, setIsLegendAnimating] = useState(false);
  const token = localStorage.getItem('token');
  
  // Check if user is admin
  const isAdmin = Boolean(
    user?.is_admin === true ||
    (user?.role != null && String(user.role).trim().toLowerCase() === 'admin')
  );

  // Auto-hide the feature guide after 3 seconds on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLegendAnimating(true);
      // Small delay to allow animation to complete before hiding
      setTimeout(() => {
        setShowLegend(false);
        setIsLegendAnimating(false);
      }, 300);
    }, 3000);

    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        const response = await fetch(`${API_URL}/templates`);
        if (response.ok) {
          const data = await response.json();
          setTemplates(data);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  // Create whiteboard from template
  const createFromTemplate = async (templateId, templateName) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in first');
      return;
    }

    setIsCreatingFromTemplate(true);
    try {
      // Step 1: Create a new whiteboard
      const createResponse = await axios.post(
        `${API_URL}/whiteboards`,
        { name: `${templateName}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newWhiteboardId = createResponse.data.id;

      // Step 2: Apply the template to it
      const applyResponse = await axios.post(
        `${API_URL}/whiteboards/${newWhiteboardId}/apply-template`,
        { template_id: templateId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (applyResponse.status === 200) {
        toast.success(`Created "${templateName}" whiteboard`);
        navigate(`/whiteboard/${newWhiteboardId}`);
      }
    } catch (error) {
      console.error('Error creating from template:', error);
      toast.error('Failed to create whiteboard from template');
    } finally {
      setIsCreatingFromTemplate(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      brainstorm: Brain,
      process: GitBranch,
      project: Trello,
      strategy: Target,
      meeting: FileText,
    };
    return icons[category] || LayoutGrid;
  };
  
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatRelativeDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleDeleteWhiteboard = async (e, whiteboardId) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this whiteboard?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/whiteboards/${whiteboardId}`);
      toast.success('Whiteboard deleted');
      onRefresh?.();
    } catch (error) {
      console.error('Failed to delete whiteboard:', error);
      toast.error('Failed to delete whiteboard');
    }
  };

  return (
    <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-slate-900 min-w-0" data-testid="dashboard-content">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">My Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">Overview of your tasks and recent activity</p>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Feature Legend Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (showLegend) {
                  setIsLegendAnimating(true);
                  setTimeout(() => {
                    setShowLegend(false);
                    setIsLegendAnimating(false);
                  }, 300);
                } else {
                  setShowLegend(true);
                }
              }}
              className="text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
            >
              <Info size={16} />
              <span className="hidden sm:inline">Feature Guide</span>
              {showLegend && !isLegendAnimating ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Button>
          </div>
        </div>
        
        {/* Collapsible Feature Legend - Drawer Animation */}
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showLegend && !isLegendAnimating 
              ? 'max-h-48 opacity-100 mt-4' 
              : 'max-h-0 opacity-0 mt-0'
          }`}
        >
          <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {featureLegend.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.name} className="flex items-start gap-3">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${feature.color}15` }}
                    >
                      <Icon size={16} style={{ color: feature.color }} />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{feature.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">{feature.description}</p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{feature.visibility}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard 
          icon={LayoutGrid}
          value={stats.assigned_to_me}
          label="Assigned to me"
          iconColor="#2563EB"
          iconBg="#EFF6FF"
        />
        <StatCard 
          icon={CheckCircle2}
          value={stats.completed_this_week}
          label="Completed this week"
          iconColor="#22C55E"
          iconBg="#F0FDF4"
        />
        <StatCard 
          icon={Clock}
          value={stats.due_today}
          label="Due today"
          iconColor="#F97316"
          iconBg="#FFF7ED"
        />
        <StatCard 
          icon={AlertTriangle}
          value={stats.overdue}
          label="Overdue"
          iconColor="#EF4444"
          iconBg="#FEF2F2"
        />
      </div>

      {/* Canvas Section */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6 sm:mb-8" data-testid="whiteboards-section">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <Palette className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">Canvas</h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
            {/* Search Input - full width on mobile */}
            <div className="relative w-full sm:w-auto min-w-0" data-testid="whiteboard-search">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search whiteboards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8 py-2 w-full sm:w-64 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="whiteboard-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                  data-testid="clear-search-btn"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                onClick={() => navigate('/whiteboard/new')}
                className="bg-blue-600 hover:bg-blue-700 text-white flex-1 sm:flex-initial"
                data-testid="new-whiteboard-btn"
              >
                <Plus size={16} className="mr-1" />
                New Whiteboard
              </Button>

              {/* Create from Template dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#2563EB] text-[#2563EB] hover:bg-blue-50 flex-1 sm:flex-initial"
                    disabled={isCreatingFromTemplate}
                    data-testid="create-from-template-btn"
                  >
                    {isCreatingFromTemplate ? (
                      <Loader2 size={16} className="mr-1 animate-spin" />
                    ) : (
                      <LayoutGrid size={16} className="mr-1" />
                    )}
                    From Template
                    <ChevronDown size={14} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Choose a Template</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isLoadingTemplates ? (
                    <div className="p-4 text-center text-gray-500">
                      <Loader2 size={20} className="mx-auto animate-spin mb-2" />
                      Loading templates...
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No templates available
                    </div>
                  ) : (
                    templates.map((template) => {
                      const IconComponent = getCategoryIcon(template.category);
                      return (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => createFromTemplate(template.id, template.name)}
                          className="cursor-pointer"
                          data-testid={`template-${template.id}`}
                        >
                          <IconComponent size={16} className="mr-2 text-gray-500" />
                          <div className="flex-1">
                            <div className="font-medium">{template.name}</div>
                            <div className="text-xs text-gray-400">{template.description}</div>
                          </div>
                        </DropdownMenuItem>
                      );
                    })
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {(() => {
          // Filter whiteboards based on search
          const filteredWhiteboards = whiteboards.filter(wb => 
            wb.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
          
          if (whiteboards.length === 0) {
            return (
              <div className="text-center py-12 bg-gray-50 dark:bg-slate-800/50 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
                <Palette className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-3">No whiteboards yet</p>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/whiteboard/new')}
                  data-testid="create-first-whiteboard-btn"
                >
                  Create your first whiteboard
                </Button>
              </div>
            );
          }
          
          if (filteredWhiteboards.length === 0 && searchQuery) {
            return (
              <div className="text-center py-12 bg-gray-50 dark:bg-slate-800/50 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
                <Search className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-1">No whiteboards matching &quot;{searchQuery}&quot;</p>
                <button 
                  onClick={() => setSearchQuery('')}
                  className="text-blue-600 dark:text-blue-400 text-sm hover:underline"
                >
                  Clear search
                </button>
              </div>
            );
          }
          
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredWhiteboards.map((wb) => (
                <div
                  key={wb.id}
                  className="group relative p-4 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/whiteboard/${wb.id}`)}
                  data-testid={`whiteboard-card-${wb.id}`}
                >
                  {/* Thumbnail placeholder */}
                  <div className="h-24 bg-white dark:bg-slate-700 rounded-md border border-gray-200 dark:border-gray-600 mb-3 flex items-center justify-center overflow-hidden">
                    <div className="text-gray-400 dark:text-gray-500 text-xs">
                      {wb.objects?.length || 0} objects
                    </div>
                  </div>
                  
                  <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">{wb.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Edited {formatRelativeDate(wb.updated_at)}
                  </p>
                  
                  {/* Actions dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 w-8 h-8 p-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity touch-manipulation"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={(e) => handleDeleteWhiteboard(e, wb.id)}
                      >
                        <Trash2 size={14} className="mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Shared Canvas Section */}
      {sharedWhiteboards.length > 0 && (
        <div className="mb-8" data-testid="shared-whiteboards-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-green-500" />
              Shared with me
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sharedWhiteboards.map((wb) => (
              <div
                key={wb.id}
                className="group relative p-4 bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-slate-800 rounded-xl border border-green-200 dark:border-green-800 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/whiteboard/${wb.id}`)}
                data-testid={`shared-whiteboard-card-${wb.id}`}
              >
                {/* Thumbnail placeholder */}
                <div className="h-24 bg-white dark:bg-slate-700 rounded-md border border-gray-200 dark:border-gray-600 mb-3 flex items-center justify-center overflow-hidden">
                  <div className="text-gray-400 dark:text-gray-500 text-xs">
                    {wb.objects?.length || 0} objects
                  </div>
                </div>
                
                <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">{wb.name}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Edited {formatRelativeDate(wb.updated_at)}
                </p>
                
                {/* Shared badge */}
                <div className="absolute top-2 right-2 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 text-xs font-medium px-2 py-0.5 rounded-full">
                  Shared
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* My Tasks */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6" data-testid="my-tasks-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">My Tasks</h2>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsTaskModalOpen(true)}
              className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              data-testid="create-task-btn"
            >
              <Plus size={16} className="mr-1" />
              Add Task
            </Button>
          </div>
          
          {myTasks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 dark:text-gray-500">No tasks assigned to you</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-4"
                onClick={() => setIsTaskModalOpen(true)}
              >
                <Plus size={14} className="mr-1" />
                Create your first task
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {myTasks.map((task) => (
                <div key={task.id} className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{task.title}</p>
                    {task.due_date && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Due: {task.due_date}</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    task.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                    task.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' :
                    'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  }`}>
                    {task.priority}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6" data-testid="recent-activity-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h2>
          </div>
          
          {activities.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 dark:text-gray-500">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {activities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                    style={{ backgroundColor: activity.user_avatar_color || '#2563EB' }}
                  >
                    {activity.user_name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white">
                      <span className="font-medium">{activity.user_name}</span>
                      {' '}{activity.action}{' '}
                      <span className="text-blue-600 dark:text-blue-400 font-medium">{activity.target_name}</span>
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {formatDate(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Access */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6" data-testid="quick-access-card">
        <div className="flex items-center gap-2 mb-4">
          <Grid3X3 className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Access</h2>
        </div>
        
        {quickAccess.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400 dark:text-gray-500">No boards yet. Create your first board!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickAccess.map((item) => (
              <button
                key={item.id}
                className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg text-left hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors border border-gray-100 dark:border-gray-600"
              >
                <h3 className="font-medium text-gray-900 dark:text-white text-sm">{item.name}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {item.hub_name} • {item.task_count} cards
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Task Create Modal */}
      <TaskCreateModal
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        token={token}
        onTaskCreated={() => {
          toast.success('Task created successfully');
          onRefresh?.();
        }}
      />
    </main>
  );
};

const StatCard = ({ icon: Icon, value, label, iconColor, iconBg }) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-5 flex items-center gap-3 sm:gap-4 min-w-0">
      <div
        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: iconBg }}
      >
        <Icon size={24} style={{ color: iconColor }} />
      </div>
      <div className="min-w-0">
        <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">{value}</p>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">{label}</p>
      </div>
    </div>
  );
};
